import { store } from "../store/store";
import { openErrorAlert } from "../store/Slices/ErrorAlertSlice";
import axios from "axios";

type ShowAppErrorInput =
  | string
  | {
      title?: string;
      message: string;
      details?: string;
    }
  | unknown;

function serializeError(error: ShowAppErrorInput): {
  title: string;
  message: string;
  details: string;
} {
  if (typeof error === "string") {
    return { title: "Error", message: error, details: error };
  }

  if (
    error &&
    typeof error === "object" &&
    "message" in error &&
    typeof (error as { message: unknown }).message === "string" &&
    ("title" in error || "details" in error) &&
    !axios.isAxiosError(error)
  ) {
    const payload = error as {
      title?: string;
      message: string;
      details?: string;
    };
    return {
      title: payload.title || "Error",
      message: payload.message,
      details: payload.details || payload.message,
    };
  }

  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const url = [error.config?.baseURL, error.config?.url]
      .filter(Boolean)
      .join("");
    const method = (error.config?.method || "").toUpperCase();
    const data = error.response?.data as { message?: string } | undefined;
    const message =
      data?.message ||
      error.message ||
      (status ? `Request failed with status ${status}` : "Network error");

    return {
      title: status ? `Error ${status}` : "Network error",
      message,
      details: JSON.stringify(
        {
          message: error.message,
          code: error.code,
          method,
          url,
          status,
          statusText: error.response?.statusText,
          responseData: error.response?.data,
          time: new Date().toISOString(),
        },
        null,
        2,
      ),
    };
  }

  if (error instanceof Error) {
    return {
      title: "Error",
      message: error.message,
      details: error.stack || error.message,
    };
  }

  return {
    title: "Error",
    message: "Something went wrong",
    details: JSON.stringify(error, null, 2),
  };
}

/** Unified debug error alert (OK + Copy). Later can filter to network-only. */
export function showAppError(error: ShowAppErrorInput) {
  const payload = serializeError(error);
  store.dispatch(openErrorAlert(payload));
}
